import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useProjectAnalysis } from '../../pages/project/useProjectAnalysis';
import InfoHint from '../common/InfoHint';

/**
 * TCF breakdown + Block-2 complexity factors. These describe the airport's spatial
 * / taxiway complexity, so they live with the "Mapas" module (next to the airport
 * map) rather than the operational-KPIs module. Reads apcas.block2 via the shared
 * analysis hook.
 */
const TcfComplexityPanel: React.FC = () => {
  const { language } = useLanguage();
  const { apcas, safeNum } = useProjectAnalysis();
  const es = language === 'es';

  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';
  const tcf = apcas.block2?.tcf || 0;

  return (
    <div className="space-y-6">
      {/* TCF Breakdown — illustrative fixed split, NOT a measured decomposition. */}
      <div className="pro-card p-6">
        <h3 className={`font-bold text-lg mb-1 flex items-center gap-1.5 ${textMain}`}>
          {es ? 'Desglose TCF (Taxiway Complexity Factor)' : 'TCF Breakdown (Taxiway Complexity Factor)'}
          <InfoHint
            align="left"
            text={es
              ? 'El TCF mide cuánto se complica el rodaje. Este desglose es ILUSTRATIVO: reparte el TCF con pesos fijos (50/30/20) entre parados, rodaje lento y variabilidad — NO son tres cantidades medidas. La descomposición real por velocidad de rodaje (parados <5 kt, etc.) se calcula en el pipeline pero aún no se expone aquí; hasta entonces esto es orientativo, no un dato validado.'
              : 'The TCF measures how much taxiing gets complicated. This breakdown is ILLUSTRATIVE: it splits the TCF with fixed weights (50/30/20) across stopped, slow taxi and variability — these are NOT three measured quantities. The real per-taxi-speed decomposition (stopped <5 kt, etc.) is computed in the pipeline but not surfaced here yet; until then this is indicative, not a validated figure.'}
          />
        </h3>
        <p className={`text-xs mb-4 ${textSub}`}>
          {es ? 'Reparto ilustrativo por pesos fijos (50/30/20), no medido' : 'Illustrative fixed-weight split (50/30/20), not measured'}
        </p>
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 rounded-lg" style={{ background: 'color-mix(in srgb, var(--aipron-accent) 8%, transparent)' }}>
            <p className={`text-xs uppercase font-bold ${textSub}`}>Stopped (CI)</p>
            <p className="text-2xl font-black font-mono tabular-nums" style={{ color: 'var(--aipron-accent)' }}>
              {(tcf * 0.5 * 100).toFixed(1)}%
            </p>
            <p className={`text-xs ${textSub}`}>{es ? 'Velocidad <5 knots' : 'Speed <5 knots'}</p>
          </div>
          <div className="p-4 rounded-lg" style={{ background: 'color-mix(in srgb, var(--aipron-warning) 8%, transparent)' }}>
            <p className={`text-xs uppercase font-bold ${textSub}`}>Slow Taxi</p>
            <p className="text-2xl font-black font-mono tabular-nums" style={{ color: 'var(--aipron-warning)' }}>
              {(tcf * 0.3 * 100).toFixed(1)}%
            </p>
            <p className={`text-xs ${textSub}`}>{es ? 'Velocidad 5-10 knots' : 'Speed 5-10 knots'}</p>
          </div>
          <div className="p-4 rounded-lg" style={{ background: 'color-mix(in srgb, var(--aipron-info) 8%, transparent)' }}>
            <p className={`text-xs uppercase font-bold ${textSub}`}>Speed Variability</p>
            <p className="text-2xl font-black font-mono tabular-nums" style={{ color: 'var(--aipron-info)' }}>
              {(tcf * 0.2 * 100).toFixed(1)}%
            </p>
            <p className={`text-xs ${textSub}`}>{es ? 'Coef. variación' : 'Variation coef.'}</p>
          </div>
        </div>
      </div>

      {/* Complexity Factors Summary */}
      <div className="pro-card p-6">
        <h3 className={`font-bold text-lg mb-4 flex items-center gap-1.5 ${textMain}`}>
          {es ? 'Factores de Complejidad (Block 2)' : 'Complexity Factors (Block 2)'}
          <InfoHint
            align="left"
            text={es
              ? 'Los cinco factores que penalizan la capacidad de plataforma por complejidad operativa: TCF (rodaje), WCF (meteorología), WIF (obras/NOTAM), NOF (operación nocturna) y CCF (cobertura CNS). Cada uno pesa distinto; combinados dan el CF Total, el multiplicador que reduce la RAC.'
              : 'The five factors that penalise apron capacity through operational complexity: TCF (taxiing), WCF (weather), WIF (works/NOTAM), NOF (night ops) and CCF (CNS coverage). Each has its own weight; combined they give the CF Total, the multiplier that reduces RAC.'}
          />
        </h3>
        <div className="space-y-3">
          {[
            { label: 'TCF (Taxiway)', value: apcas.block2?.tcf || 0, weight: apcas.block2?.weights?.tcf || 0.35, color: 'var(--aipron-accent)' },
            { label: 'WCF (Weather)', value: apcas.block2?.wcf || 0, weight: apcas.block2?.weights?.wcf || 0.25, color: 'var(--aipron-info)' },
            { label: 'WIF (Works/NOTAM)', value: apcas.block2?.wif || 0, weight: apcas.block2?.weights?.wif || 0.20, color: 'var(--aipron-danger)' },
            { label: 'NOF (Night Ops)', value: apcas.block2?.nof || 0, weight: apcas.block2?.weights?.nof || 0.10, color: '#8B5CF6' },
            { label: 'CCF (CNS Coverage)', value: apcas.block2?.ccf || 0, weight: apcas.block2?.weights?.ccf || 0.10, color: 'var(--aipron-success)' },
          ].map(f => (
            <div key={f.label} className="flex items-center gap-4">
              <span className={`w-32 text-sm font-medium ${textSub}`}>{f.label}</span>
              <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: 'var(--surface-inset)' }}>
                <div className="h-full" style={{ width: `${Math.min(f.value * 100, 100)}%`, background: f.color }} />
              </div>
              <span className={`w-20 text-right text-sm font-bold font-mono tabular-nums ${textMain}`}>{(f.value * 100).toFixed(1)}%</span>
              <span className={`w-16 text-right text-xs font-mono tabular-nums ${textSub}`}>w={f.weight.toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-[var(--border)]">
          <div className="flex justify-between items-center">
            <span className={`font-bold ${textMain}`}>CF Total (Complexity Factor)</span>
            <span className="text-2xl font-black font-mono tabular-nums" style={{ color: 'var(--aipron-accent)' }}>
              {safeNum(apcas.block2?.cf_total || 1.0, 3)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TcfComplexityPanel;
